import React, {ChangeEvent, useState} from "react";
import {
    Button,
    Card,
    Checkbox,
    CheckboxOnChangeData,
    Dialog,
    DialogActions,
    DialogBody,
    DialogContent,
    DialogSurface,
    DialogTrigger,
    Dropdown,
    Field,
    makeStyles,
    Option,
    Spinner,
    Table,
    TableBody,
    TableCell,
    TableCellActions,
    TableHeader,
    TableHeaderCell,
    TableRow,
    Text,
    Title3,
    tokens
} from "@fluentui/react-components";
import {DatePicker} from "@fluentui/react-datepicker-compat";
import {TimePicker, TimeSelectionData, TimeSelectionEvents} from "@fluentui/react-timepicker-compat";
import {
    ArrowSync24Regular,
    Delete24Regular,
    DocumentTableArrowRight24Regular,
    Play24Regular
} from "@fluentui/react-icons";
import {useApiMutation, useApiQuery} from "../../services/useApiQuery.ts";
import {Group, Module, Session} from "../../services/types.ts";
import {useSetAtom} from "jotai/index";
import {errorAtom} from "../../store.ts";
import {formatDate} from "../../dateFormatter.ts";
import {useTranslation} from "react-i18next";

const useStyles = makeStyles({
    container: {
        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
        padding: '16px',
    },
    header: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '16px',
        gap: '16px',
        '@media (max-width: 640px)': {
            flexDirection: 'column',
            alignItems: 'flex-start',
        },
    },
    headerButtonsContainer: {
        display: 'flex',
        gap: '8px',
        '@media (max-width: 640px)': {
            width: '100%',
            flexDirection: 'column',
            '& button': {
                width: '100%',
            },
        },
    },
    actionArea: {
        display: 'flex',
        alignItems: 'flex-end',
        gap: '16px',
        marginTop: '16px',
        padding: '16px',
        backgroundColor: tokens.colorNeutralBackground2,
        borderRadius: '4px',
    },
    formField: {
        display: 'flex',
        flexDirection: 'column',
        gap: '4px',
        minWidth: '250px',
    },
    startButton: {
        marginLeft: 'auto',
    },
    errorContainer: {
        padding: '16px',
        backgroundColor: tokens.colorPaletteRedBackground1,
        borderRadius: '4px',
        marginBottom: '16px',
    },
    successContainer: {
        padding: '16px',
        backgroundColor: tokens.colorPaletteGreenBackground1,
        borderRadius: '4px',
        marginBottom: '16px',
    },
    modalForm: {
        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
    },
    datePickerWrapper: {
        position: 'relative',
        zIndex: 100,
    },
    dialogContent: {
        overflow: 'visible',
    },
    dialogTitle: {
        color: tokens.colorNeutralForeground1,
    },
    responsiveTable: {
        '@media (max-width: 640px)': {
            '& table': {
                display: 'block',
                width: '100%',
            },
            '& thead': {
                display: 'none',
            },
            '& tbody': {
                display: 'block',
                width: '100%',
            },
            '& tr': {
                display: 'flex',
                flexDirection: 'column',
                padding: '12px 0',
                marginBottom: '8px',
                '&:not(:last-child)': {
                    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
                    paddingBottom: '16px',
                },
            },
            '& td': {
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                border: 'none',
                padding: '4px 0',
                width: '100%',
                position: 'relative',
                '&::before': {
                    content: 'attr(data-label)',
                    fontWeight: 'bold',
                    marginRight: '8px',
                },
                '&:last-child': {
                    justifyContent: 'flex-end',
                    marginTop: '8px',
                },
            },
            '& [data-label="Actions"]': {
                paddingTop: '8px',
                marginTop: '8px',
            },
            '& [role="cell-actions"]': {
                opacity: '1 !important',
                // @ts-ignore
                position: 'static !important',
                transform: 'none !important',
                padding: '0 !important',
            },
        },
    },
});

export const CampaignsTab: React.FC = () => {
    const styles = useStyles();
    const {t} = useTranslation();
    const setErrorMessage = useSetAtom(errorAtom);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [newSessionData, setNewSessionData] = useState({
        moduleId: "",
        groupId: "",
        startDate: new Date(),
        testMode: false,
        timeOfDay: {
            hours: 9,
            minutes: 0
        }
    });

    const {
        data: sessions,
        isLoading: sessionsLoading,
        isRefetching: isRefetchingSessions,
        refetch: refetchSessions
    } = useApiQuery<Session[]>('/api/session/list');

    const {
        data: groups,
        isPending: groupsLoading,
        refetch: refetchGroups,
        isRefetching: isRefetchingGroups,
    } = useApiQuery<Group[]>('/api/group/list');

    const {
        data: availableModules,
        isLoading: modulesLoading,
    } = useApiQuery<Module[]>('/api/module/list');

    const startSessionMutation = useApiMutation<Module[]>('POST', {
        onSuccess: async () => {
            handleCloseModal();
            setErrorMessage(null);
            await refetchSessions();
        },
        onError: (error) => {
            if (error.message.includes("409")) {
                setErrorMessage(t("error.campaign-exists"));
            } else {
                setErrorMessage(`Failed to start session: ${error.message}`);
            }
            setIsModalOpen(false);
        }
    });

    const deleteSessionMutation = useApiMutation<void>('DELETE', {
        onSuccess: async () => {
            setErrorMessage(null);
            await refetchSessions();
        },
        onError: (error) => {
            setErrorMessage(`Failed to delete session: ${error.message}`);
        }
    });

    /*const downloadReportMutation = useApiMutation<Blob>('POST', {
        onSuccess: async (data) => {
            alert(JSON.stringify(data));
        },
        onError: (error) => {
            setErrorMessage(`Failed to download report: ${error.message}`);
        }
    });*/

    const handleModalGroupChange = (_: any, data: { selectedOptions: string[] }) => {
        setNewSessionData(prev => ({
            ...prev,
            groupId: data.selectedOptions[0] || ""
        }));
        setErrorMessage(null);
    };

    const handleModalModuleChange = (_: any, data: { selectedOptions: string[] }) => {
        setNewSessionData(prev => ({
            ...prev,
            moduleId: data.selectedOptions[0] || ""
        }));
        setErrorMessage(null);
    };

    const handleStartDateChange = (date: Date | null | undefined) => {
        if (date) {
            setNewSessionData(prev => ({
                ...prev,
                startDate: date
            }));
            setErrorMessage(null);
        }
    };
    
    const handleTestModeChange = (ev: ChangeEvent<HTMLInputElement>, data: CheckboxOnChangeData) => {
        setNewSessionData(prev => ({
            ...prev,
            testMode: data.checked !== 'mixed' && data.checked
        }));
        setErrorMessage(null);
    };


    const handleCreateSession = () => {
        if (!newSessionData.moduleId || !newSessionData.groupId) {
            setErrorMessage(t("error.choose-message-series-and-audience"));
            setIsModalOpen(false)
            return;
        }

        const localTime = new Date();
        localTime.setHours(newSessionData.timeOfDay.hours);
        localTime.setMinutes(newSessionData.timeOfDay.minutes);

        const utcHours = localTime.getUTCHours();
        const utcMinutes = localTime.getUTCMinutes();

        startSessionMutation.mutate({
            path: "/api/session/start",
            body: {
                moduleId: newSessionData.moduleId,
                groupId: newSessionData.groupId,
                scheduledAtDate: newSessionData.startDate.toISOString(),
                testMode: newSessionData.testMode,
                timeOfDay: {
                    hours: utcHours,
                    minutes: utcMinutes
                }
            }
        });
    };

    const handleDeleteSession = (sessionId: string) => {
        deleteSessionMutation.mutate({
            path: "/api/session/delete",
            body: {
                sessionId
            }
        });
    };

    const resetModalState = () => {
        setNewSessionData({
            moduleId: "",
            groupId: "",
            startDate: new Date(),
            testMode: false,
            timeOfDay: {
                hours: 9,
                minutes: 0
            }
        });
        setErrorMessage(null);
    };

    const handleTimeChange = (_: TimeSelectionEvents, data: TimeSelectionData) => {
        if (data.selectedTime) {
            setNewSessionData(prev => ({
                ...prev,
                timeOfDay: {
                    hours: data.selectedTime?.getHours() || 12,
                    minutes: data.selectedTime?.getMinutes() || 0
                }
            }));
            setErrorMessage(null);
        }
    };

    const handleOpenModal = () => {
        resetModalState();
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        resetModalState();
    };


    return <Card>
        <div className={styles.container}>
            <div className={styles.header}>
                <Title3>{t("nav.campaigns")}</Title3>
                <div className={styles.headerButtonsContainer}>
                    <Dialog open={isModalOpen} onOpenChange={(_, data) => setIsModalOpen(data.open)}>
                        <DialogTrigger disableButtonEnhancement>
                            <Button appearance="primary" icon={<DocumentTableArrowRight24Regular/>}
                                    onClick={handleOpenModal}>
                                {t("button.start-new-campaign")}
                            </Button>
                        </DialogTrigger>
                        <DialogSurface>
                            <DialogBody>
                                <Title3 className={styles.dialogTitle}>{t("button.start-new-campaign")}</Title3>
                                <DialogContent className={styles.dialogContent}>
                                    <div className={styles.modalForm}>
                                        <Field label={t("field.message-series")} required>
                                            <Dropdown
                                                placeholder={t("placeholder.choose-series")}
                                                value={availableModules?.find(module => module.id === newSessionData.moduleId)?.name || newSessionData.moduleId || ""}
                                                selectedOptions={newSessionData.moduleId ? [newSessionData.moduleId] : []}
                                                onOptionSelect={handleModalModuleChange}
                                                disabled={modulesLoading || !availableModules?.length}
                                            >
                                                {availableModules?.map(module => (
                                                    <Option key={module.id} value={module.id}>
                                                        {module.name}
                                                    </Option>
                                                ))}
                                            </Dropdown>
                                        </Field>

                                        <Field label={t("field.audience")} required>
                                            <Dropdown
                                                placeholder={t("placeholder.choose-audience")}
                                                value={groups?.find(group => group.id === newSessionData.groupId)?.name || newSessionData.groupId || ""}
                                                selectedOptions={newSessionData.groupId ? [newSessionData.groupId] : []}
                                                onOptionSelect={handleModalGroupChange}
                                                disabled={groupsLoading || !groups?.length}
                                            >
                                                {groups?.map(group => (
                                                    <Option key={group.id} value={group.id}>
                                                        {group.name}
                                                    </Option>
                                                ))}
                                            </Dropdown>
                                        </Field>

                                        <Field label={t("field.start-date")} required>
                                            <DatePicker
                                                value={newSessionData.startDate}
                                                onSelectDate={handleStartDateChange}
                                                minDate={new Date()}
                                                formatDate={(date: any) => date?.toLocaleDateString() || ''}
                                            />
                                        </Field>

                                        <Field label={`${t("field.message-time")} (${Intl.DateTimeFormat().resolvedOptions().timeZone})`} required>
                                            <TimePicker
                                                dateAnchor={newSessionData.startDate}
                                                selectedTime={new Date(0, 0, 0, newSessionData.timeOfDay.hours, newSessionData.timeOfDay.minutes)}
                                                onTimeChange={handleTimeChange}
                                                hourCycle="h24"
                                                startHour={7}
                                                endHour={18}
                                                value={(() => {
                                                    const localTime = `${(newSessionData.timeOfDay.hours + "").padStart(2, "0")}:${(newSessionData.timeOfDay.minutes + "").padStart(2, '0')}`;
                                                    // Calculate equivalent UTC time
                                                    const d = new Date();
                                                    d.setHours(newSessionData.timeOfDay.hours);
                                                    d.setMinutes(newSessionData.timeOfDay.minutes);
                                                    const utcHours = d.getUTCHours();
                                                    const utcMinutes = d.getUTCMinutes();
                                                    const utcTime = `${(utcHours + "").padStart(2, "0")}:${(utcMinutes + "").padStart(2, '0')}`;
                                                    return `${localTime} (${utcTime} UTC)`;
                                                })()}
                                            />
                                        </Field>

                                        
                                        <Field>
                                            <Checkbox
                                                label={t("field.test-mode")}
                                                checked={newSessionData.testMode}
                                                onChange={handleTestModeChange}
                                            />
                                        </Field>
                                    </div>
                                </DialogContent>
                                <DialogActions>
                                    <Button appearance="secondary" onClick={handleCloseModal}>{t("button.cancel")}</Button>
                                    <Button
                                        appearance="primary"
                                        icon={startSessionMutation.isPending ? <Spinner size="extra-tiny"/> :
                                            <Play24Regular/>}
                                        onClick={handleCreateSession}
                                        disabled={!newSessionData.moduleId || !newSessionData.groupId || startSessionMutation.isPending}
                                    >
                                        {t("button.start-campaign")}
                                    </Button>
                                </DialogActions>
                            </DialogBody>
                        </DialogSurface>
                    </Dialog>
                    <Button
                        appearance="subtle"
                        icon={(isRefetchingSessions || isRefetchingGroups) ? <Spinner size="extra-tiny"></Spinner> :
                            <ArrowSync24Regular/>}
                        onClick={() => {
                            refetchSessions();
                            refetchGroups();
                        }}
                        disabled={isRefetchingSessions || sessionsLoading || isRefetchingGroups || groupsLoading}
                    >
                        {t("button.refresh")}
                    </Button>
                </div>
            </div>

            {sessionsLoading || isRefetchingSessions ? (
                <Spinner label={t("text.loading-campaigns")}/>
            ) : sessions?.length === 0 ? (
                <Text>{t("text.no-campaigns")}</Text>
            ) : (
                <div className={styles.responsiveTable}>
                    <Table aria-label="Campaigns Table">
                        <TableHeader>
                            <TableRow>
                                <TableHeaderCell>{t("table.header.message-series")}</TableHeaderCell>
                                <TableHeaderCell>{t("table.header.audience")}</TableHeaderCell>
                                <TableHeaderCell>{t("table.header.started-at")}</TableHeaderCell>
                                <TableHeaderCell>{t("table.header.next-message-at")}</TableHeaderCell>
                                <TableHeaderCell>{t("table.header.messages-sent")}</TableHeaderCell>
                                <TableHeaderCell>{t("table.header.action")}</TableHeaderCell>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {sessions?.map((session: Session) => (
                                <TableRow key={`${session.id}`}>
                                    <TableCell data-label={t("table.header.message-series")}>{session.moduleName}</TableCell>
                                    <TableCell data-label={t("table.header.audience")}>{session.groupName}</TableCell>
                                    <TableCell data-label={t("table.header.started-at")}>{formatDate(session.startedAt)}
                                    </TableCell>
                                    <TableCell data-label={t("table.header.next-message-at")}>{formatDate(session.nextMessageAt)}
                                    </TableCell>
                                    <TableCell data-label={t("table.header.messages-sent")}>{`${session.deliveredContacts} / ${session.totalContacts}`}</TableCell>
                                    <TableCell data-label={t("table.header.action")}>
                                        <TableCellActions role="cell-actions">
                                            {/*<Button appearance="secondary"
                                                    onClick={() => downloadReportMutation.mutate({
                                                        path: `/api/analytics/report`,
                                                        body: {
                                                            campaignId: session.id,
                                                        }
                                                    })}
                                                    disabled={downloadReportMutation.isPending}>
                                                Download Report
                                            </Button>*/}
                                            <Button appearance="secondary"
                                                    icon={<Delete24Regular/>}
                                                    onClick={() => handleDeleteSession(session.id)}>
                                                {t("button.delete")}
                                            </Button>
                                        </TableCellActions>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </div>
            )}
        </div>
    </Card>
}
